import React, { ChangeEvent, forwardRef, useCallback, useState } from 'react'

import { SxProps } from '@mui/material'

import TextField from 'components_new/atoms/TextField'

import { TVariant, getErrorMessage, getLabel, getVariantProps } from './utils'
import { useTranslation } from 'language/translator'

type TInputState = {
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setValue: React.Dispatch<React.SetStateAction<string>>
  value: string
}

type TextInputProps = TInputState & {
  autoComplete?: string
  label?: string
  required?: boolean
  sx?: SxProps
  variant?: TVariant
}

export function useTextInputState(): TInputState {
  const [value, setValue] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  return { error, setError, setValue, value }
}

/**
 * TextInput
 *
 * A controlled input component built on top of MUI's TextField.
 * It works together with the `useTextInputState` hook, which provides
 * `value`, `setValue`, `error`, and `setError` for managing input state.
 *
 * ## Usage
 * ```tsx
 * const email = useTextInputState()
 *
 * <TextInput required variant="email" {...email} />
 * ```
 *
 * ## Features
 * - Controlled input: always syncs value and error with parent state
 * - Validation: runs on blur for all variants, live validation for `email`
 * - Translated labels and error messages via `useTranslation`
 * - Pass-through of additional MUI TextField props via `...restProps`
 *
 *
 * ## Extensibility
 * New variants can be added by extending `TVariant`, `getErrorMessage`,
 * `getLabel`, and `getVariantProps`. Examples: password, phone number,
 * numeric input, etc.
 */
const TextInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextInputProps
>((props: TextInputProps, ref) => {
  const {
    error,
    label,
    setValue,
    setError,
    value,
    variant = 'default',
    ...restProps
  } = props

  const { t } = useTranslation()

  const runValidation = useCallback(
    (val: string) => getErrorMessage(val, variant, t),
    [variant, t]
  )

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value

      setValue(newValue)

      if (variant === 'email') {
        // if email we want live validation
        setError(runValidation(newValue))
      }
    },
    [error, setValue, setError, runValidation, variant]
  )

  const onBlur = useCallback(() => {
    setError(runValidation(value))
  }, [runValidation, setError, value])

  return (
    <TextField
      ref={ref}
      label={getLabel(label, variant, t)}
      onBlur={onBlur}
      onChange={onChange}
      error={Boolean(error)}
      helperText={error}
      value={value}
      {...getVariantProps(variant)}
      {...restProps}
    />
  )
})

export default TextInput
